// Group Payroll Overview (comp/payroll authz Phase 5b) — one row per company the viewer can
// see, for one pay period: run status, headcount, totals and approvals still pending.
//
// ⚠️ Three rules this file exists to keep:
//  1. Companies come from GET /legal-entities, which the server filters to the viewer's
//     reach. A failure is an ERROR, never "no companies".
//  2. A money field the server omitted (absent/null — the viewer may not see it) is
//     'restricted', never 0, and is NEVER added into a sum: a company total with one
//     restricted run is itself restricted.
//  3. A company with no run in the period says so — no run is not a zero payroll.
import { gatewayApi } from '@/lib/hrApi';

const API = '/api/v1';

export interface OverviewCompany {
  id: string;
  name: string;
  country?: string;
  base_currency?: string;
}

/** The run list read model, with every figure optional — the server may withhold any. */
export interface OverviewRunView {
  id: string;
  legal_entity_id: string;
  period_start: string;
  period_end?: string;
  state: string;
  currency?: string;
  label?: string;
  employee_count?: number | null;
  total_gross?: string | null;
  total_net?: string | null;
  total_employer_cost?: string | null;
}

export interface OverviewApproval {
  level: string;
  decision: string;
  label?: string;
  scope?: string;
}

/** Companies in the viewer's reach. Throws on failure (an error is not an empty list). */
export const listVisibleCompanies = () =>
  gatewayApi
    .get<{ legal_entities: OverviewCompany[] }>(`${API}/legal-entities`)
    .then((r) => r.data?.legal_entities ?? []);

export const listOverviewRuns = () =>
  gatewayApi.get<{ runs: OverviewRunView[] }>(`${API}/payroll-runs`).then((r) => r.data?.runs ?? []);

/**
 * The approval chain the server reports for a run — GET /payroll-runs/{id}/approvals:
 *   { run_id, approvals: [{ level, decision, label?, scope?, actor_id?, decided_at?,
 *                           calc_digest?, note? }] }
 * Decided rows carry actor_id/decided_at/calc_digest/note; PENDING rows carry none of
 * them; a group-scoped row carries scope: 'group'. Nothing else (role, order, …) is
 * promised, so nothing else is read.
 */
export function parseOverviewApprovals(body: unknown): OverviewApproval[] {
  const b = body as { approvals?: unknown; data?: { approvals?: unknown } } | unknown[] | null | undefined;
  const raw = Array.isArray(b) ? b : Array.isArray(b?.approvals) ? b.approvals : Array.isArray(b?.data?.approvals) ? b.data.approvals : null;
  // A 200 without a chain is NOT "nothing pending" — the caller shows it as unknown.
  if (!raw) throw new Error('The approval chain came back in an unexpected shape.');
  return raw
    .filter((a): a is Record<string, unknown> => !!a && typeof a === 'object' && typeof (a as { level?: unknown }).level === 'string')
    .map((a) => ({
      level: a.level as string,
      decision: typeof a.decision === 'string' && a.decision ? a.decision : 'pending',
      label: typeof a.label === 'string' ? a.label : undefined,
      scope: typeof a.scope === 'string' ? a.scope : undefined,
    }));
}

export const getOverviewApprovals = (runId: string) =>
  gatewayApi.get(`${API}/payroll-runs/${runId}/approvals`).then((r) => parseOverviewApprovals(r.data));

// ---------------------------------------------------------------------------------------
// Pure aggregation

/** A money figure: a decimal string, or 'restricted' when the server withheld it. */
export type Figure = { kind: 'value'; amount: number } | { kind: 'restricted' };

/** Approvals still to give on a run awaiting approval. 'loading' = not fetched yet;
 *  'unknown' = the chain couldn't load. Neither ever reads as "none pending". */
export type PendingApprovals =
  | { kind: 'not_submitted' }
  | { kind: 'loading' }
  | { kind: 'none' }
  | { kind: 'pending'; count: number; next: string; nextIsGroup: boolean }
  | { kind: 'unknown' };

export interface OverviewRunCell {
  id: string;
  state: string;
  label?: string;
  headcount: number | null; // null = not reported
  pending: PendingApprovals;
}

export interface OverviewRow {
  company: OverviewCompany;
  currency: string | null;
  runs: OverviewRunCell[]; // active (non-cancelled) runs in the period
  cancelledOnly: boolean; // only cancelled runs exist for the period
  gross: Figure | null; // null = no active run
  net: Figure | null;
  employerCost: Figure | null;
}

export interface OverviewSummary {
  companies: number;
  companiesWithRun: number;
  runsAwaitingApproval: number;
  /** Pending approval steps across runs whose chain loaded; `unknownRuns` couldn't load. */
  pendingSteps: number;
  unknownRuns: number;
}

/** YYYY-MM of a run's period (period_start is a civil date; no Date parsing). */
export const periodKey = (periodStart: string) => (periodStart ?? '').slice(0, 7);

/** Distinct periods present in the runs, newest first. */
export function overviewPeriods(runs: OverviewRunView[]): string[] {
  return Array.from(new Set(runs.map((r) => periodKey(r.period_start)).filter(Boolean))).sort((a, b) =>
    b.localeCompare(a),
  );
}

const present = (v: unknown): v is string | number =>
  v !== undefined && v !== null && v !== '' && Number.isFinite(Number(v));

/** Sum a field over runs — restricted if ANY run withholds it. Never a partial sum. */
export function sumFigure(values: Array<string | number | null | undefined>): Figure {
  let total = 0;
  for (const v of values) {
    if (!present(v)) return { kind: 'restricted' };
    total += Number(v);
  }
  return { kind: 'value', amount: total };
}

const LEVEL_NAME: Record<string, string> = { hr: 'HR', finance: 'Finance', group: 'Group Finance', final: 'Final sign-off' };

export function pendingFrom(state: string, approvals: OverviewApproval[] | 'error' | undefined): PendingApprovals {
  if (state === 'draft') return { kind: 'not_submitted' };
  if (state !== 'validated') return { kind: 'none' };
  // Not fetched yet is NOT a failure: "Couldn't load" while the request is still in
  // flight was the dev defect's visible symptom.
  if (approvals === undefined) return { kind: 'loading' };
  if (approvals === 'error') return { kind: 'unknown' };
  const open = approvals.filter((a) => a.decision !== 'approved');
  if (open.length === 0) return { kind: 'none' };
  const next = open[0];
  return {
    kind: 'pending',
    count: open.length,
    next: next.label?.trim() || LEVEL_NAME[next.level] || next.level,
    nextIsGroup: next.scope === 'group' || next.level === 'group',
  };
}

export function buildGroupOverview(
  companies: OverviewCompany[],
  runs: OverviewRunView[],
  period: string,
  approvalsByRun: Record<string, OverviewApproval[] | 'error'>,
): { rows: OverviewRow[]; summary: OverviewSummary } {
  const inPeriod = runs.filter((r) => periodKey(r.period_start) === period);
  const rows: OverviewRow[] = [...companies]
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((company) => {
      const mine = inPeriod.filter((r) => r.legal_entity_id === company.id);
      const active = mine.filter((r) => r.state !== 'cancelled');
      const cells: OverviewRunCell[] = active.map((r) => ({
        id: r.id,
        state: r.state,
        label: r.label?.trim() || undefined,
        headcount: typeof r.employee_count === 'number' && Number.isFinite(r.employee_count) ? r.employee_count : null,
        pending: pendingFrom(r.state, approvalsByRun[r.id]),
      }));
      const has = active.length > 0;
      return {
        company,
        currency: active[0]?.currency ?? company.base_currency ?? null,
        runs: cells,
        cancelledOnly: !has && mine.length > 0,
        gross: has ? sumFigure(active.map((r) => r.total_gross)) : null,
        net: has ? sumFigure(active.map((r) => r.total_net)) : null,
        employerCost: has ? sumFigure(active.map((r) => r.total_employer_cost)) : null,
      };
    });

  const allCells = rows.flatMap((r) => r.runs);
  const summary: OverviewSummary = {
    companies: rows.length,
    companiesWithRun: rows.filter((r) => r.runs.length > 0).length,
    runsAwaitingApproval: allCells.filter((c) => c.state === 'validated').length,
    pendingSteps: allCells.reduce((n, c) => n + (c.pending.kind === 'pending' ? c.pending.count : 0), 0),
    unknownRuns: allCells.filter((c) => c.pending.kind === 'unknown').length,
  };
  return { rows, summary };
}
